/**
 * 69.58 step 2, the sound (ARCHITECTURE §5.29; the sheet sound_sheets/aircraft_defence.html): a ship's countermeasures, heard —
 * the rules, while `countermeasureSound.ts` voices the flares; play hears them by the leans (game/actionSound.ts: a
 * `decoyPop`, a `shipIntercept` from core/countermeasures.ts; before the switch-on, neither made a sound).
 *   - **The flare salvo** (`decoyPop`, at the ship): the dispenser's cartridges fired in a quick string (`FLARE_SALVO`), then
 *     each flare's burning hiss for its burn (COUNTERMEASURES.burnSeconds), heard from where it was popped. A flare falls
 *     slowly (`fallPeople` over its burn) and the ship flies on, so the hiss is a one-shot from the pop's place, not a voice
 *     that follows the flare.
 *   - **The gunship's roof turret** (`shipIntercept`, a burst every GUNSHIP.turret.interval from a drum of `bursts`): an
 *     autocannon heard as point defence's guns are (autocannonMix `CannonVoice`, `hearCannon`, the gate on the bursts, the
 *     rotary's spin, the misses' self-destructs — the sheet's; play hears them once it draws them), from the ship. A kill
 *     (`hit`) is a small blast at the missile (before, a killed missile vanished without a sound).
 *   - **A missile on a flare** is the missile's own burst, already heard (its `blast`, the LAW's class) where the flare was;
 *     the sheet sets an airburst's voice beside it.
 * Pure: no audio here, no state written. Unplayed guesses (decision 121): the owner's ear decides.
 */
import { COUNTERMEASURES } from '../constants/action';
import { GUNSHIP } from '../constants/gunship';
import { CANNON_VOICES, type CannonVoice } from './autocannonMix';
import { METRES_PER_PERSON, SPEED_OF_SOUND, type Heard } from './gunfireMix';

/** The flares' two styles: `thump`, dull cartridge thuds and a roaring hiss; `crack`, sharper cracks in a quicker string and a brighter fizz that crackles. */
export type FlareStyle = 'thump' | 'crack';
export const FLARE_STYLES: readonly FlareStyle[] = ['thump', 'crack'];
/** A salvo's cartridges: how many, how far apart (s); the burn's length (the sim's). */
export const FLARE_SALVO: Record<FlareStyle, { cartridges: number; apart: number; burn: number }> = {
  thump: { cartridges: 4, apart: 0.07, burn: COUNTERMEASURES.burnSeconds },
  crack: { cartridges: 6, apart: 0.04, burn: COUNTERMEASURES.burnSeconds },
};
export type FlarePart = 'pop' | 'hiss';
/** Each part's loudness at the ship (× the rifle's report), its reach (people: half there) and its range (people). The pops are a dispenser's cartridges: under a rifle's report at the ship, carrying a little further (they go off in the open sky), always under the .50's; the hiss is quieter still and dies sooner (its high band). */
export const FLARE_HEARD: Record<FlarePart, { gain: number; reach: number; range: number }> = {
  pop: { gain: 0.8, reach: 12, range: 900 },
  hiss: { gain: 0.35, reach: 15, range: 600 },
};
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** A flare's part `people` away on its `pan` side; null past its range. */
export function hearFlare(part: FlarePart, people: number, pan: number): Heard | null {
  const k = FLARE_HEARD[part], d = Math.max(0, people);
  if (d > k.range) return null;
  return {
    gain: k.gain / (1 + d / k.reach),
    cutoff: clamp(14000 * Math.exp(-d / (part === 'hiss' ? 160 : 220)), 700, 14000),
    delay: (d * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: clamp(pan, -1, 1),
    tail: 0.15 + 0.5 * clamp(d / 300, 0, 1),
  };
}

/**
 * The roof turret's two voices. `ciws`: point defence's CIWS as it is (the M61, 75 rounds a second, the owner's "CIWs sound
 * is good"), its gate on the turret's bursts. `light`: a lighter mount — a smaller rotary, higher and thinner, a quicker
 * spin, carrying less.
 */
export type TurretStyle = 'ciws' | 'light';
export const TURRET_STYLES: readonly TurretStyle[] = ['ciws', 'light'];
export const TURRET_VOICES: Record<TurretStyle, CannonVoice> = {
  ciws: { ...CANNON_VOICES.ciws, interval: GUNSHIP.turret.interval },
  light: {
    rate: 50, crackHz: 1900, growl: 0.4, roarHz: 820, gain: 1.1, range: 1.8, tail: 1.4,
    rotary: { whineRest: 360, whineFull: 1500, whine: 0.06, spinUp: 0.4, spinDown: 1.2 },
    interval: GUNSHIP.turret.interval, gapShare: 1.6, selfDestruct: { count: 2, fuse: [0.8, 1.4], level: 0.8, reach: 22, range: 800 },
  },
};

/** A missile's burst on a flare: `burst`, the LAW's burst as play hears it now; `airburst`, the burst with the ground's thump taken out and a longer roll, as a blast in the open sky is. */
export type FlareBurstStyle = 'burst' | 'airburst';
export const FLARE_BURST_STYLES: readonly FlareBurstStyle[] = ['burst', 'airburst'];

/** The owner's picks (DECISIONS 291: "Lean crack for countermeasure sound ... ciws for turret"); the burst as today. */
export const COUNTERMEASURE_STYLE: { flare: FlareStyle; turret: TurretStyle; burst: FlareBurstStyle } = { flare: 'crack', turret: 'ciws', burst: 'burst' };
